import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import Helmet from 'react-helmet';

type MetaItem = {
  name: string;
  content: string;
};

type SEOProps = {
  title?: string;
  description?: string;
  url?: string;
  lang?: string;
  author?: string;
  telephone?: string;
  keywords?: Array<string>;
  meta?: Array<MetaItem>;
  image?: string;
};

const SEO: React.FC<SEOProps> = (props: SEOProps) => {
  const { site } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
          description
          author
          telephone
          url
          keywords
          image
        }
      }
    }
  `);

  const { siteMetadata } = site;

  const keywords = siteMetadata.keywords || [];
  const meta = siteMetadata.meta || [];
  const lang = siteMetadata.lang;
  const siteTitle = props.title || siteMetadata.title;
  const siteDescription = props.description || siteMetadata.description;
  const siteUrl = props.url || siteMetadata.url;
  const siteAuthor = props.author || siteMetadata.author;
  const siteTelephone = props.telephone || siteMetadata.telephone;
  const siteImage = props.image || siteMetadata.image;
  const siteKeywords = [...keywords, props.keywords].join(',');

  const metaData = [
    {
      name: 'canonical',
      content: siteUrl,
    },
    {
      name: 'description',
      content: siteDescription,
    },
    {
      name: 'image',
      content: siteImage,
    },
    {
      name: 'og:url',
      content: siteUrl,
    },
    {
      name: 'og:type',
      content: 'article',
    },
    {
      name: 'og:title',
      content: siteTitle,
    },
    {
      name: 'og:description',
      content: siteDescription,
    },
    {
      name: 'og:image',
      content: siteImage,
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:creator',
      content: siteAuthor,
    },
    {
      name: 'twitter:title',
      content: siteTitle,
    },
    {
      name: 'twitter:description',
      content: siteDescription,
    },
    {
      name: 'twitter:image',
      content: siteImage,
    },
    {
      name: 'keywords',
      content: siteKeywords,
    },
  ].concat(meta);

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  const schemaOrgWebPage = {
    '@context': 'http://schema.org',
    '@type': 'Person',
    url: siteUrl,
    name: siteAuthor,
    jobTitle: 'Voiceover artist',
    telephone: siteTelephone,
    gender: 'female',
  };

  const linkData = [
    {
      rel: 'shortcut icon',
      href: 'icons/favicon-512x512.png',
    },
  ];

  return (
    <Helmet
      htmlAttributes={{ lang: lang || 'en' }}
      defaultTitle={siteTitle}
      title={siteTitle}
      titleTemplate={`%s | ${siteTitle}`}
      meta={metaData}
      link={linkData}
    >
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgWebPage)}
      </script>
    </Helmet>
  );
};

export default SEO;
